import {
  ADMIN_TOKEN_KEY,
  ADMIN_USER_KEY,
  type AdminClient,
  type AdminDeposit,
  type AdminTradingAccount,
  type AdminUser,
} from './types'
import { apiUrl } from '../lib/api-base'
import { clearAuthSession, getAuthItem } from '../shared/authStorage'

type ApiError = { code?: string; message?: string }

export class AdminApiError extends Error {
  code?: string
  status?: number

  constructor(message: string, opts?: { code?: string; status?: number }) {
    super(message)
    this.name = 'AdminApiError'
    this.code = opts?.code
    this.status = opts?.status
  }
}

function safeFailureMessage(raw: string | undefined, status: number, hadAuth = false): string {
  const msg = (raw || '').trim()
  const lower = msg.toLowerCase()
  // Allow normal auth / password-change UX copy.
  const isAuthUx =
    lower.includes('credential') ||
    lower.includes('sign in') ||
    lower.includes('email or password') ||
    lower.includes('invalid email') ||
    lower.includes('incorrect password') ||
    lower.includes('current password') ||
    lower.includes('new password') ||
    lower.includes('password confirmation') ||
    lower.includes('password must')
  if (
    !msg ||
    lower === 'invalid request' ||
    lower.includes('traceback') ||
    lower.includes('exception') ||
    (lower.includes('internal') && !isAuthUx) ||
    lower.includes('stack') ||
    lower.includes('secret') ||
    (lower.includes('password') && !isAuthUx)
  ) {
    if ((status === 401 || status === 403) && hadAuth) return 'Admin session expired'
    if (status === 401 || status === 403) {
      return 'Unable to sign in. Check your credentials and try again.'
    }
    if (status === 404) return 'Resource not found'
    if (status === 429) return 'Too many requests. Please try again shortly.'
    if (status >= 500) return 'Service temporarily unavailable'
    return 'Unable to complete request'
  }
  return msg
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  opts: { auth?: boolean } = {},
): Promise<T> {
  const useAuth = opts.auth !== false
  const token = useAuth ? getAuthItem(ADMIN_TOKEN_KEY) : null
  const headers = new Headers(init.headers || {})
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json')
  }
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let res: Response
  try {
    res = await fetch(apiUrl(path), { ...init, headers })
  } catch {
    throw new AdminApiError('Unable to reach the server', { code: 'NETWORK' })
  }

  const data = await res.json().catch(() => ({}))
  const err = (data as { error?: ApiError }).error
  if (res.status === 401 || res.status === 403) {
    const code = (err?.code || '').toUpperCase()
    const sessionDead =
      !code ||
      code === 'UNAUTHORIZED' ||
      code === 'UNAUTHENTICATED' ||
      code === 'FORBIDDEN' ||
      code === 'ADMIN_DISABLED'
    // Only wipe session for real auth failures — not wrong-password form errors.
    if (token && sessionDead) {
      clearAuthSession(ADMIN_TOKEN_KEY, ADMIN_USER_KEY)
      throw new AdminApiError('Admin session expired', {
        code: err?.code || 'UNAUTHORIZED',
        status: res.status,
      })
    }
    throw new AdminApiError(safeFailureMessage(err?.message, res.status, Boolean(token)), {
      code: err?.code || 'UNAUTHORIZED',
      status: res.status,
    })
  }
  if (!res.ok) {
    throw new AdminApiError(safeFailureMessage(err?.message, res.status, Boolean(token)), {
      code: err?.code,
      status: res.status,
    })
  }
  return data as T
}

function queryString(params: Record<string, string | number | boolean | undefined> = {}) {
  const q = new URLSearchParams()
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') q.set(k, String(v))
  })
  const s = q.toString()
  return s ? `?${s}` : ''
}

export type AdminNotificationRow = {
  id: number
  audience: string
  channel?: string
  subject: string
  title: string
  body: string
  ctaLabel?: string
  ctaPath?: string
  recipientCount: number
  sentCount: number
  skippedCount: number
  failedCount: number
  status: string
  errorSummary?: string
  adminEmail?: string
  createdAt?: string
}

export const adminApi = {
  login(email: string, password: string) {
    return request<{ accessToken: string; admin?: AdminUser }>(
      '/api/admin/login',
      {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      },
      { auth: false },
    )
  },

  changePassword(currentPassword: string, newPassword: string) {
    return request<{ success: boolean; message?: string }>('/api/admin/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    })
  },

  forgotPassword(email: string) {
    return request<{ success: boolean; message?: string }>(
      '/api/admin/forgot-password',
      {
        method: 'POST',
        body: JSON.stringify({ email }),
      },
      { auth: false },
    )
  },

  resetPassword(token: string, newPassword: string) {
    return request<{ success: boolean; message?: string }>(
      '/api/admin/reset-password',
      {
        method: 'POST',
        body: JSON.stringify({ token, newPassword }),
      },
      { auth: false },
    )
  },

  listClients(params: Record<string, string | number | boolean | undefined> = {}) {
    return request<{
      success: boolean
      clients: AdminClient[]
      pagination?: { page?: number; total_pages?: number; total?: number; page_size?: number }
    }>(`/api/admin/clients${queryString(params)}`)
  },

  getClient(id: number) {
    return request<{ success: boolean; client: AdminClient & Record<string, unknown> }>(
      `/api/admin/clients/${id}`,
    )
  },

  clientAccounts(id: number) {
    return request<{ success: boolean; accounts: AdminTradingAccount[] }>(
      `/api/admin/clients/${id}/trading-accounts`,
    )
  },

  clientTimeline(id: number) {
    return request<{ success: boolean; events: Array<Record<string, unknown>> }>(
      `/api/admin/clients/${id}/timeline`,
    )
  },

  setClientStatus(id: number, action: string, reason: string) {
    return request(`/api/admin/clients/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ action, reason }),
    })
  },

  accountSummary(id: number) {
    return request<{ success: boolean; account: Record<string, unknown> }>(
      `/api/admin/trading-accounts/${id}/summary`,
    )
  },

  accountOpenPositions(id: number) {
    return request<{
      success: boolean
      positions?: Array<Record<string, unknown>>
      sync_state?: string
      last_sync_at?: string
    }>(`/api/admin/trading-accounts/${id}/open-positions`)
  },

  accountPendingOrders(id: number) {
    return request<{
      success: boolean
      orders?: Array<Record<string, unknown>>
      sync_state?: string
      last_sync_at?: string
    }>(`/api/admin/trading-accounts/${id}/pending-orders`)
  },

  accountTradeHistory(id: number, params: Record<string, string | number | undefined> = {}) {
    return request<{
      success: boolean
      trades?: Array<Record<string, unknown>>
      sync_state?: string
      last_sync_at?: string
      pagination?: { page?: number; total_pages?: number; total?: number }
    }>(`/api/admin/trading-accounts/${id}/trade-history${queryString(params)}`)
  },

  accountActivity(id: number, params: Record<string, string | number | undefined> = {}) {
    return request<{
      success: boolean
      activities?: Array<Record<string, unknown>>
      sync_state?: string
      last_sync_at?: string
      pagination?: { page?: number; total_pages?: number; total?: number }
    }>(`/api/admin/trading-accounts/${id}/account-activity${queryString(params)}`)
  },

  setAccountStatus(id: number, action: string, reason: string) {
    return request(`/api/admin/trading-accounts/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ action, reason }),
    })
  },

  listDeposits(params: Record<string, string | number | boolean | undefined> = {}) {
    return request<{
      success: boolean
      deposits: AdminDeposit[]
      pagination?: { page?: number; total_pages?: number; total?: number; page_size?: number }
    }>(`/api/admin/deposits${queryString(params)}`)
  },

  getDeposit(id: number) {
    return request<{
      success: boolean
      deposit: AdminDeposit & Record<string, unknown>
      timeline?: Array<Record<string, unknown>>
      has_screenshot?: boolean
      client?: Record<string, unknown>
    }>(`/api/admin/deposits/${id}`)
  },

  approveDeposit(id: number, reason: string) {
    return request(`/api/admin/deposits/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ action: 'APPROVE', reason }),
    })
  },

  rejectDeposit(id: number, reason: string) {
    return request(`/api/admin/deposits/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ action: 'REJECT', reason }),
    })
  },

  refreshDeposit(id: number) {
    return request(`/api/admin/deposits/${id}/refresh`, { method: 'POST' })
  },

  /** Relative API path — fetch with Authorization; do not put tokens in the URL. */
  depositScreenshotUrl(id: number) {
    return `/api/admin/deposits/${id}/screenshot`
  },

  listWithdrawals(params?: { status?: string; limit?: number }) {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.limit) q.set('limit', String(params.limit))
    const qs = q.toString()
    return request<{
      success: boolean
      withdrawals: Array<Record<string, unknown>>
    }>(`/api/admin/withdrawals${qs ? `?${qs}` : ''}`)
  },

  getWithdrawal(id: number) {
    return request<{
      success: boolean
      withdrawal: Record<string, unknown>
      timeline?: Array<Record<string, unknown>>
    }>(`/api/admin/withdrawals/${id}`)
  },

  approveWithdrawal(id: number, payoutTxid?: string) {
    return request(`/api/admin/withdrawals/${id}/approve`, {
      method: 'POST',
      body: JSON.stringify({ payoutTxid: payoutTxid || undefined }),
    })
  },

  markWithdrawalProcessing(id: number, payoutTxid: string) {
    return request(`/api/admin/withdrawals/${id}/processing`, {
      method: 'POST',
      body: JSON.stringify({ payoutTxid }),
    })
  },

  completeWithdrawal(id: number, payoutTxid?: string) {
    return request(`/api/admin/withdrawals/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ payoutTxid: payoutTxid || undefined }),
    })
  },

  rejectWithdrawal(id: number, reason: string) {
    return request(`/api/admin/withdrawals/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    })
  },

  listSystemBackups() {
    return request<{
      success: boolean
      status: {
        enabled?: boolean
        ready?: boolean
        scriptReady?: boolean
        sudoReady?: boolean
        retain?: number
        backupDir?: string
      }
      backups: Array<{
        id: string
        sizeBytes?: number
        createdAt?: string | null
        label?: string
        scope?: string
        sha256?: string
        includes?: string[]
      }>
    }>('/api/admin/system/backups')
  },

  createSystemBackup(label = 'manual', scope: 'full-vps' | 'perfectpredict' = 'full-vps') {
    return request<{
      success: boolean
      backup: {
        id?: string
        createdAt?: string
        label?: string
        scope?: string
        sha256?: string
        sizeBytes?: number
      }
    }>('/api/admin/system/backups', {
      method: 'POST',
      body: JSON.stringify({ label, scope }),
    })
  },

  deleteSystemBackup(id: string) {
    return request<{ success: boolean }>(`/api/admin/system/backups/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    })
  },

  health() {
    return request<{
      status?: string
      service?: string
      environment?: string
      mt5Environment?: string
    }>('/health')
  },

  bridgeStatus() {
    return request<{
      success: boolean
      bridge: {
        configured: boolean
        reachable: boolean
        mt5Ready: boolean
        status: string
        message: string
      }
      control: {
        pending?: boolean
        reconnectRequestedAt?: string | null
        reconnectAckedAt?: string | null
        lastWatchdogAt?: string | null
        lastWatchdogResult?: string | null
      }
    }>('/api/admin/bridge/status')
  },

  bridgeReconnect() {
    return request<{
      success: boolean
      message?: string
      bridge: {
        configured: boolean
        reachable: boolean
        mt5Ready: boolean
        status: string
        message: string
      }
      control: {
        pending?: boolean
        reconnectRequestedAt?: string | null
        reconnectAckedAt?: string | null
        lastWatchdogAt?: string | null
        lastWatchdogResult?: string | null
      }
    }>('/api/admin/bridge/reconnect', { method: 'POST' })
  },

  ibApplications(status?: string) {
    return request<{ success: boolean; applications: Array<Record<string, unknown>> }>(
      `/api/admin/ib/applications${queryString({ status })}`,
    )
  },

  ibApproveApplication(
    applicationId: number,
    body: {
      code?: string
      profitSharePercent: number
      lossSharePercent: number
      minimumReserve: number
      depositCommissionPercent?: number
      commissionHoldDays?: number
    },
  ) {
    return request<{ success: boolean; subadmin: Record<string, unknown> }>(
      `/api/admin/ib/applications/${applicationId}/approve`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  },

  ibSetCommissionTerms(
    subadminId: number,
    body: { depositCommissionPercent: number; commissionHoldDays: number },
  ) {
    return request<{ success: boolean; subadmin: Record<string, unknown> }>(
      `/api/admin/ib/subadmins/${subadminId}/commission-terms`,
      { method: 'POST', body: JSON.stringify(body) },
    )
  },

  ibRejectApplication(applicationId: number, note = '') {
    return request<{ success: boolean; application: Record<string, unknown> }>(
      `/api/admin/ib/applications/${applicationId}/reject`,
      { method: 'POST', body: JSON.stringify({ note }) },
    )
  },

  ibSubadmins() {
    return request<{ success: boolean; subadmins: Array<Record<string, unknown>> }>(
      '/api/admin/ib/subadmins',
    )
  },

  ibSubadminClients(subadminId: number) {
    return request<{
      success: boolean
      clientCount: number
      subadmin: Record<string, unknown>
      clients: Array<{
        id: number
        email?: string
        phone?: string
        firstName?: string
        lastName?: string
        status?: string
        createdAt?: string
        accounts: Array<Record<string, unknown>>
      }>
    }>(`/api/admin/ib/subadmins/${subadminId}/clients`)
  },

  ibManualCredit(subadminId: number, amount: number, note = '') {
    return request<{ success: boolean }>(`/api/admin/ib/subadmins/${subadminId}/credit`, {
      method: 'POST',
      body: JSON.stringify({ amount, note }),
    })
  },

  ibWalletRequests(status = 'PENDING') {
    return request<{ success: boolean; requests: Array<Record<string, unknown>> }>(
      `/api/admin/ib/wallet-requests${queryString({ status })}`,
    )
  },

  ibApproveWalletRequest(requestId: number) {
    return request<{ success: boolean }>(`/api/admin/ib/wallet-requests/${requestId}/approve`, {
      method: 'POST',
    })
  },

  ibRejectWalletRequest(requestId: number, note = '') {
    return request<{ success: boolean }>(`/api/admin/ib/wallet-requests/${requestId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    })
  },

  notificationsPreview(audience: string) {
    return request<{
      success: boolean
      audience: string
      recipientCount: number
      clientCount: number
      ibCount: number
      capped: boolean
      maxRecipients: number
      smtpConfigured: boolean
    }>(`/api/admin/notifications/preview${queryString({ audience })}`)
  },

  notificationsHistory(limit = 30) {
    return request<{ success: boolean; notifications: AdminNotificationRow[] }>(
      `/api/admin/notifications${queryString({ limit })}`,
    )
  },

  notificationsSend(body: {
    audience: string
    channel?: string
    subject: string
    title: string
    body: string
    ctaLabel?: string
    ctaPath?: string
  }) {
    return request<{ success: boolean; notification: AdminNotificationRow }>(
      '/api/admin/notifications/send',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    )
  },

  dashboard() {
    return request<{
      success: boolean
      kpis: {
        clientsTotal: number
        clientsVerified: number
        clientsSuspended: number
        accountsTotal: number
        accountsDemo: number
        accountsLive: number
        accountsActive: number
        pendingDeposits: number
        stuckCryptoDeposits: number
        todayDeposits: number
        todayCompletedDeposits: number
        completedDeposits: number
        pendingWithdrawals: number
        completedWithdrawals: number
        ibApplicationsPending: number
        ibActive: number
        ibWalletRequestsPending: number
      }
      bridge: {
        configured: boolean
        reachable: boolean
        mt5Ready: boolean
        demoReady?: boolean
        liveReady?: boolean
        liveStatus?: string
        status: string
        message: string
      }
      queues: {
        deposits: number
        stuckCryptoDeposits?: number
        withdrawals: number
        ibApplications: number
        ibWalletRequests: number
      }
    }>('/api/admin/dashboard')
  },

  balanceLookup(mt5Login: number) {
    return request<{
      success: boolean
      account: {
        accountId?: number
        mt5Login?: number
        mt5LoginMasked?: string
        mode?: string
        status?: string
        clientId?: number
        clientEmail?: string
        clientName?: string
        subadminId?: number | null
        subadminCode?: string
        subadminName?: string
      }
    }>(`/api/admin/balance/lookup${queryString({ mt5Login })}`)
  },

  balanceHistory(params: { q?: string; action?: string; limit?: number } = {}) {
    return request<{
      success: boolean
      items: Array<{
        id: number
        createdAt?: string
        action: string
        amount: number
        balanceBefore?: string | number | null
        balanceAfter?: string | number | null
        reason?: string
        reference?: string
        accountId?: number
        clientId?: number
        clientEmail?: string
        clientName?: string
        subadminId?: number | null
        subadminCode?: string
        subadminName?: string
        mt5Login?: number
        mt5LoginMasked?: string
        mode?: string
      }>
    }>(`/api/admin/balance/history${queryString(params)}`)
  },

  balanceAdjust(body: {
    action: 'CREDIT' | 'DEBIT'
    amount: number
    reason: string
    accountId?: number
    mt5Login?: number
  }) {
    return request<{
      success: boolean
      action: string
      amount: number
      reference: string
      accountId?: number
      clientId?: number
      mt5LoginMasked?: string
      mode?: string
      balanceBefore?: number
      balanceAfter?: number
      reason?: string
    }>('/api/admin/balance/adjust', {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  accountBalanceAdjust(
    accountId: number,
    body: { action: 'CREDIT' | 'DEBIT'; amount: number; reason: string },
  ) {
    return request<{
      success: boolean
      action: string
      amount: number
      reference: string
      mt5LoginMasked?: string
      balanceBefore?: number
      balanceAfter?: number
    }>(`/api/admin/trading-accounts/${accountId}/balance/adjust`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
}
